import React from 'react'
import PropTypes from 'prop-types'
import { Box, Rating, Text, P, ExpandableContent, Level, Media, Clipped, List } from '@deseretbook/react-ui'

/**
 * Highlighted text component. Renders text but with any section matching
 * the specified query highlighted.
 * @param {string} props.text - Text to display
 * @param {string} props.query - Query to highlight in text.
 * @returns {function} Component
 */
const HighlightedText = ({ text, query }) => {
  if (!query || !text) {
    return text || false
  }
  /**
   * Splits query by space for multiple keyword highlighting.
   * The string is joined with a '|' or "OR" statement in regex.
   */
  const multiQuery = query.split(' ').filter(Boolean).join('|')

  return text
    .replace(new RegExp(multiQuery, 'gi'), match => `|${match}|`)
    .split('|')
    .filter(Boolean)
    .map((segment) => {
      if (new RegExp(multiQuery, 'gi').test(segment)) {
        return <mark style={{ background: 'rgba(255,255,100,0.5)' }}>{segment}</mark>
      }

      return segment
    })
}

/**
 * This returns a Review Component
 * @param {obj} review - The review object
 * @param {string} keywords - Keywords used for highlighting text within reviews
 * @returns {func} Component
 */
export const Review = ({
  rating,
  name,
  createdAt,
  review,
  title,
  verifiedPurchaser,
  keywords,
}) => {
  /**
   * Returns a Date string in the form of the users locale set
   * by default on their browser. US = jan 14, 2019
   * @returns {string} a formated date string
   */
  const formatDate = () => {
    const date = new Date(createdAt)
    const options = { year: 'numeric', month: 'short', day: 'numeric' }

    return date.toLocaleDateString(undefined, options)
  }

  return (
    <Media>
      <Media.Content>
        <Level>
          <Level.Left>
            <Level.Item>
              <Rating average={rating} />
            </Level.Item>
          </Level.Left>
          <Level.Item>
            <Clipped>
              <Text hasEllipsis isBold title={title}>
                <HighlightedText text={title} query={keywords} />
              </Text>
            </Clipped>
          </Level.Item>
        </Level>
        <Box paddingBottom={0.5}>
          <List type="horizontal">
            <List.Item>
              <Text isItalic>{name || 'Anonymous'}</Text>
            </List.Item>
            <List.Item>
              <Text>{formatDate()}</Text>
            </List.Item>
            {verifiedPurchaser && (
              <List.Item>
                <Text isBold color="primary">Verified Purchaser</Text>
              </List.Item>
            )}
          </List>
        </Box>
        <ExpandableContent collapsedHeight={6}>
          <P><HighlightedText text={review} query={keywords} /></P>
        </ExpandableContent>
      </Media.Content>
    </Media>
  )
}

Review.propTypes = {
  rating: PropTypes.number,
  name: PropTypes.string,
  createdAt: PropTypes.string,
  review: PropTypes.string,
  title: PropTypes.string,
  verifiedPurchaser: PropTypes.bool,
  keywords: PropTypes.string,
}
